import { useState } from 'react'
import { DANGER, DARK, GOLD, GREY_300, GREY_400, WHITE } from '@/lib/genesis'

interface ConfirmDialogProps {
  trigger: (open: () => void) => React.ReactNode
  title: string
  description: string
  confirmLabel: string
  destructive?: boolean
  onConfirm: () => Promise<void> | void
  // children: optional extra form content rendered between the description
  // and the Cancel/Confirm buttons (ADR-186's Resolve-with-optional-note
  // idiom is the first consumer -- a small textarea the confirm action
  // reads from via the caller's own state, not anything ConfirmDialog
  // manages itself).
  children?: React.ReactNode
}

/**
 * Minimal confirmation dialog (no headless-UI/Radix dependency -- kept
 * dependency-light since this Phase 5B slice does not adopt @atfai/ui,
 * which is not resolvable outside the bun workspace apps-future/console
 * sits outside of; see final report). Used for every license
 * suspend/resume/revoke action per the task brief's requirement that each
 * mutation gets an explicit confirmation, with Revoke additionally styled
 * as destructive/terminal.
 *
 * Three-surface UI alignment pass (three-surface-ui-alignment): restyled
 * onto Genesis tokens + glass-card idiom.
 */
export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  return (
    <>
      {trigger(() => setOpen(true))}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass-card w-full max-w-md p-5">
            <h2 className="font-sub mb-2 text-base font-semibold" style={{ color: WHITE }}>{title}</h2>
            <p className="mb-4 text-sm" style={{ color: GREY_400 }}>{description}</p>
            {children}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="glass-button rounded-md px-3 py-1.5 text-sm"
                style={{ color: GREY_300 }}
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                style={
                  destructive
                    ? { backgroundColor: DANGER, color: DARK }
                    : { backgroundColor: GOLD, color: DARK }
                }
                disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await onConfirm()
                    setOpen(false)
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                {busy ? 'Working…' : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
